/**
 * The estimate screen's per-account breakdown (frame 132:555, "Para onde vão
 * os créditos"): the accounts driving this campaign's cost. Same columns and
 * the same wire shape as the retired `/burn-rate` page's own table -- every
 * figure is `row.estimate`, already computed on the wire.
 */

import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { credits } from '../../../i18n/format';
import { Absent } from '../../../chrome/measure';
import { EmptyState } from '../../../chrome/state';
import { DataTable } from '../../../chrome/table';
import type { BurnRate } from '../../control/api/schemas';
import { et } from './messages';

type Row = BurnRate['per_account'][number];

function priced(value: number | null): string {
  return value === null ? et('unpriced') : credits(value, getLocale());
}

const COLUMNS: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: 'handle',
    header: et('credits.column.account'),
    cell: (info) => {
      const handle = info.getValue<Row['handle']>();
      return handle ?? <Absent reason={{ text: et('credits.column.handleNone') }} />;
    },
  },
  {
    id: 'perDay',
    accessorFn: (row) => row.estimate.credits_per_day,
    header: et('credits.column.perDay'),
    meta: { numeric: true },
    cell: (info) => priced(info.getValue<number | null>()),
  },
  {
    id: 'per30d',
    accessorFn: (row) => row.estimate.credits_per_30d,
    header: et('credits.column.per30d'),
    meta: { numeric: true },
    cell: (info) => priced(info.getValue<number | null>()),
  },
];

export function CreditDestination(props: { rows: BurnRate['per_account'] }): ReactNode {
  return (
    <DataTable<Row>
      columns={COLUMNS}
      data={props.rows}
      getRowId={(row) => row.tracked_account_id}
      initialSorting={[{ id: 'perDay', desc: true }]}
      caption={et('credits.table.caption')}
      emptyState={<EmptyState title={et('empty.title')} description={et('empty.body')} />}
    />
  );
}
